import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import keishIcon from "@/assets/icon-green.svg";
import keishIconWhite from "@/assets/icon-white.svg";
import ContactSheet from "@/components/ContactSheet";
import { useTheme } from "@/components/ThemeProvider";
import SEO from "@/components/SEO";
import coterie1 from "@/assets/shop/coterie-1.jpeg";
import coterie2 from "@/assets/shop/coterie-2.jpg";
import coterie3 from "@/assets/shop/coterie-3.jpg";
import coterie4 from "@/assets/shop/coterie-4.jpg";
import coterie5 from "@/assets/shop/coterie-5.jpg";

const images = [coterie1, coterie2, coterie3, coterie4, coterie5];

const CoterieCaseStudy = () => {
  const { mode } = useTheme();
  const isDark = mode === "dark";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Coterie — Real Estate Template"
        description="A modern Framer template for real estate professionals with full CMS control over listings, community pages, and room styles."
        canonical="/work/coterie"
      />

      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-5 md:px-10 lg:px-14 py-4 backdrop-blur-xl border-b transition-colors duration-500"
        style={{
          backgroundColor: isDark ? "hsla(0, 0%, 9%, 0.9)" : "hsla(45, 100%, 98%, 0.9)",
          borderColor: isDark ? "hsla(27, 61%, 92%, 0.1)" : "hsl(45, 10%, 88%)",
        }}
      >
        <Link to="/shop" className="flex items-center gap-2 group">
          <ArrowLeft size={14} className="opacity-50 group-hover:opacity-100 transition-opacity" />
          <img src={isDark ? keishIconWhite : keishIcon} alt="KEISH" className="w-6 h-6" />
          <span className="font-body text-ts-2xs font-semibold tracking-[0.2em] uppercase">Shop</span>
        </Link>
        <ContactSheet>
          <button className="font-body text-ts-2xs tracking-[0.15em] uppercase opacity-50 hover:opacity-100 transition-opacity">
            Work With Us
          </button>
        </ContactSheet>
      </header>

      {/* Hero */}
      <div className="pt-28 md:pt-36 pb-16 md:pb-24">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
          >
            <p className="font-body text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground mb-3">
              Framer Template Creation — Real Estate
            </p>
            <h1 className="font-display text-5xl md:text-7xl italic text-foreground mb-4">
              Coterie
            </h1>
            <p className="font-body text-ts-sm text-muted-foreground tracking-wide max-w-[560px] leading-relaxed">
              A sleek, customizable template designed for real estate professionals who want a polished, standout online presence — without the cookie-cutter look.
            </p>

            <div className="flex items-center gap-4 mt-8">
              <a
                href="https://contra.com/payment-link/D8Ql5hHf-coterie-real-estate-industry-template"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-body text-ts-2xs font-medium tracking-[0.15em] uppercase transition-colors hover:bg-primary/90"
              >
                Buy for $65
                <ArrowUpRight size={12} />
              </a>
              <a
                href="https://coterie.framer.website/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 font-body text-ts-2xs tracking-[0.1em] uppercase text-muted-foreground hover:text-foreground transition-colors"
              >
                Live Preview
                <ExternalLink size={10} />
              </a>
            </div>
          </motion.div>
        </div>
      </div>


      {/* Content sections */}
      <div className="max-w-[700px] mx-auto px-5 md:px-10 pb-20 md:pb-28 space-y-16">
        {/* About */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-display text-2xl md:text-3xl italic text-foreground mb-4">
            Built for agents who want control
          </h2>
          <p className="font-body text-ts-xs text-foreground/80 leading-relaxed mb-4">
            Coterie is a modern, refined template built for real estate professionals who want a website that feels as polished as the properties they represent. With a clean design and flexible customization options, it helps you create a site that reflects your expertise without feeling generic.
          </p>
          <p className="font-body text-ts-xs text-foreground/80 leading-relaxed">
            No fluff, just a well-crafted foundation to showcase your listings and brand with confidence. Every page, every listing, every detail — managed through Framer's built-in CMS.
          </p>
        </motion.section>

        {/* CMS emphasis */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-display text-2xl md:text-3xl italic text-foreground mb-4">
            Complete CMS control
          </h2>
          <p className="font-body text-ts-xs text-foreground/80 leading-relaxed mb-6">
            Every piece of content is CMS-driven. Your team adds listings, updates community info, and manages room styles directly from Framer — no developer handoff, no code edits, no waiting.
          </p>
          <ul className="space-y-3">
            {[
              "Property listings managed entirely through CMS collections",
              "Community pages with dynamic content blocks",
              "Room styles gallery with filterable categories",
              "Contact forms and CTAs pre-wired and ready",
              "SEO metadata auto-generated per listing",
            ].map((item) => (
              <li
                key={item}
                className="font-body text-ts-2xs text-muted-foreground flex items-start gap-3"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                {item}
              </li>
            ))}
          </ul>
        </motion.section>

        {/* Features grid */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-display text-2xl md:text-3xl italic text-foreground mb-6">
            What's included
          </h2>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            {[
              "A11y Optimized",
              "Animations & Effects",
              "Automated SEO",
              "Built-in Analytics",
              "CMS Collections",
              "Reusable Components",
              "Custom Cursors",
              "Contact Forms",
              "Layout Templates",
              "Project Styles",
              "Visual Breakpoints",
              "5 Pages Included",
            ].map((feature) => (
              <div
                key={feature}
                className="font-body text-ts-2xs text-foreground/70 flex items-center gap-2 py-1.5 border-b border-border/40"
              >
                {feature}
              </div>
            ))}
          </div>
        </motion.section>

        {/* Pages */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-display text-2xl md:text-3xl italic text-foreground mb-4">
            Pages
          </h2>
          <p className="font-body text-ts-xs text-muted-foreground leading-relaxed mb-4">
            Five thoughtfully designed pages, each CMS-ready and fully responsive:
          </p>
          <div className="space-y-2">
            {["Home", "About", "Community", "Room Styles", "Contact"].map((page) => (
              <div
                key={page}
                className="font-body text-ts-xs text-foreground/80 py-2 border-b border-border/30"
              >
                {page}
              </div>
            ))}
          </div>
        </motion.section>
      </div>

      {/* Image gallery */}
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 pb-20 md:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {images.slice(1).map((img, i) => (
            <motion.img
              key={i}
              src={img}
              alt={`Coterie template screenshot ${i + 2}`}
              className="w-full rounded-lg"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              loading="lazy"
            />
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-[700px] mx-auto px-5 md:px-10 pb-24 text-center">
        <h2 className="font-display text-3xl md:text-4xl italic text-foreground mb-4">
          Ready to launch?
        </h2>
        <p className="font-body text-ts-xs text-muted-foreground mb-8">
          Grab Coterie and have your real estate site live in hours, not weeks.
        </p>
        <a
          href="https://contra.com/payment-link/D8Ql5hHf-coterie-real-estate-industry-template"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground font-body text-ts-2xs font-medium tracking-[0.15em] uppercase transition-colors hover:bg-primary/90"
        >
          Buy Template — $65
          <ArrowUpRight size={12} />
        </a>
      </div>
    </div>
  );
};

export default CoterieCaseStudy;
